import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { DatasetsViewProps } from '@databox/ember-integrations/lib/components/Datasets/View/View';
import DatasetsView from '@databox/ember-integrations/lib/components/Datasets/View/ViewEntryPoints';

import type { LineageVerifyTarget, ReactIntegrations, Service } from 'webapp-types';
import { UserRole } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { getShadowDomMode } from 'webapp/utils/common';
import type { VerificationInfo } from 'webapp/utils/verification';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';

import type { SourceOption } from 'webapp/models-custom/source-type';
import { getBillingFeature } from 'webapp/models/helpers/react-model-mapper';

interface Args {
	model: string | null;
}

interface DatasetMetric {
	id: string;
	metricKey: string;
	name: string;
	verificationInfo?: VerificationInfo | null;
}

export default class DataManagerConnectedDatasetsViewComponent extends Component<Args> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('account') declare accountService: Service.Account;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('data-manager') declare dataManagerService: Service.DataManager;
	@service('dataset-events') declare datasetEventsService: Service.DatasetEvents;
	@service('billing') declare billingService: Service.Billing;
	@service('react') declare reactService: Service.React;
	@service('verification') declare verificationService: Service.Verification;

	reactElement: ReactIntegrations.Root = null;
	invalidatedGetMetricQuery: (() => void) | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
	}

	get model(): Args['model'] {
		return this.args.model;
	}

	@action
	setupView(node: HTMLElement) {
		this.reactElement = this.reactService.create(node as HTMLDivElement);
		this.renderDatasetView();

		this.eventManagerService.on(AppEvent.ACCESS_TOKEN_REFRESHED, this, this.renderDatasetView);
		this.eventManagerService.on(AppEvent.THEME_CHANGED, this, this.renderDatasetView);
	}

	@action
	renderDatasetView() {
		if (this.reactElement && this.model !== null) {
			const { accessToken, userDataToken: dataToken } = this.currentUserService;

			const spaceAccessId = Number(this.model);

			const newProps: DatasetsViewProps = {
				isDarkThemeActive: this.navigationService.theme.isDark,
				accessToken: accessToken!,
				dataToken: dataToken!,
				spaceId: this.accountService.id,
				spaceAccessId,
				extra: {
					owner: {
						avatar: this.accountService.accountOwner!.avatar,
						email: this.accountService.accountOwner!.email,
						name: this.accountService.accountOwner!.name,
					},
					isWhitelabeled:
						this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins,
					isAiAgentHiddenForNonAdmins: this.accountService.aiAgentHiddenForNonAdmins,
					userProfile: {
						rootLevel: this.accountService.isAdminButNotInClientSpace
							? UserRole.ADMIN
							: UserRole.USER,
						rootSpaceId: this.currentUserService.rootSpaceId ?? -1,
						id: +this.currentUserService.id!,
					},
				},
				metric: {
					onCreate: () =>
						this.datasetEventsService.handleMetricCreate(
							`${spaceAccessId}`,
							this.invalidatedGetMetricQuery,
						),
					onEdit: (metric: DatasetMetric) =>
						this.datasetEventsService.handleMetricEdit(
							`${spaceAccessId}`,
							Number(metric.id),
							this.invalidatedGetMetricQuery,
						),
					onDuplicate: (metric: DatasetMetric) =>
						this.datasetEventsService.handleMetricDuplicate(
							Number(metric.id),
							this.invalidatedGetMetricQuery,
						),
					onAddToDataboard: (metric: DatasetMetric) =>
						this.datasetEventsService.handleAddToDataboard(metric.metricKey),
					onView: (metric: DatasetMetric) =>
						this.datasetEventsService.handleViewMetricInsights(metric.metricKey),
					onMetricVerificationToggle: (metric: DatasetMetric) => {
						let emberMetric = this.mmMetricsService.getMetricForKeyAndSpaceAccessId(
							metric.metricKey,
							spaceAccessId,
						);
						if (!emberMetric && metric.verificationInfo?.objectId) {
							try {
								const parsed = JSON.parse(metric.verificationInfo.objectId) as {
									key: string;
									spaceAccessId: number;
								};
								emberMetric = this.mmMetricsService.getMetricForKeyAndSpaceAccessId(
									parsed.key,
									parsed.spaceAccessId,
								);
							} catch {
								/* skip */
							}
						}
						if (emberMetric) {
							void this.mmMetricsService.toggleVerification(emberMetric).then(() => {
								this.invalidatedGetMetricQuery?.();
							});
						} else if (
							metric.verificationInfo &&
							'objectId' in metric.verificationInfo
						) {
							void this.verificationService
								.toggleVerification(metric.verificationInfo, 'metric', metric.name)
								.then(() => {
									this.invalidatedGetMetricQuery?.();
								});
						}
					},
				},
				// React navigates away on confirm: hide the dataset in the list it lands on
				onDeleteStart: () =>
					this.dataManagerService.markPendingDeletion(`${spaceAccessId}`),
				onDatasetDeleted: this.datasetEventsService.handleViewDataSources,
				onEditDataset: () =>
					this.datasetEventsService.handleEditDataset(`${spaceAccessId}`),
				onViewDataset: (spaceAccessId: number) =>
					this.datasetEventsService.handleViewDataset(`${spaceAccessId}`),
				onViewDataManager: (selectSourceOption?: string) =>
					this.datasetEventsService.handleViewDataManager(
						selectSourceOption ? (selectSourceOption as SourceOption) : undefined,
					),
				onDataRefresh: this.datasetEventsService.handleDataRefresh,
				onLineageVerify: (target: LineageVerifyTarget) =>
					this.datasetEventsService.handleLineageVerify(target).then(() => {
						this.invalidatedGetMetricQuery?.();
					}),
				getInvalidatedGetMetricQuery: (invalidatedGetMetricQuery) => {
					this.invalidatedGetMetricQuery = invalidatedGetMetricQuery;
				},
				onValidationError: this.datasetEventsService.handleValidationError,
				onDataExport: this.datasetEventsService.handleDataExport,
				onExportError: this.datasetEventsService.handleExportError,
				onDeleteSourceError: () => {
					this.dataManagerService.clearPendingDeletion(`${spaceAccessId}`);
					this.datasetEventsService.handleDeleteSourceError();
				},
				onDuplicateDataSourceError:
					this.datasetEventsService.handleDuplicateDataSourceError,
				onSourceDuplicated: this.datasetEventsService.handleSourceDuplicated,
				onConnectionUpdated: this.datasetEventsService.handleOnConnectionUpdated,
				onAddDataset: this.datasetEventsService.handleAddDataset,
				onMergeOptionsSuccess: this.datasetEventsService.handleMergeOptionsSuccess,
				onMergeOptionsError: this.datasetEventsService.handleMergeOptionsError,
				onColumnSelectionSuccess: this.datasetEventsService.handleColumnSelectionSuccess,
				onRemoveMergeDatasetSuccess:
					this.datasetEventsService.handleRemoveMergeDatasetSuccess,
				onRemoveMergeDatasetError: this.datasetEventsService.handleRemoveMergeDatasetError,
				onSemanticMetadataSaved: (datasetName: string, columnName?: string) =>
					this.datasetEventsService.handleSemanticMetadataSaved(datasetName, columnName),
				onVerificationToggle: (
					verificationInfo: Record<string, unknown>,
					objectName: string,
				) =>
					this.datasetEventsService.handleVerificationToggle(
						verificationInfo,
						objectName,
					),
				onApiError: (error: ReactIntegrations.Error) =>
					this.datasetEventsService.handleApiError(error),
				onDatasetFinishSetupSuccess:
					this.datasetEventsService.handleDatasetFinishSetupSuccess,
				onOpenActivityLog: this.datasetEventsService.openActivityLogCallback,
				onPlanUpgrade: async (featureName: string) => {
					return await this.billingService.handlePlanUpgradeForFeature(
						getBillingFeature(featureName),
					);
				},
				getInvalidatedBillingInfoQuery: (invalidatedBillingInfoQuery) => {
					this.billingService.invalidateFeatureCache = invalidatedBillingInfoQuery;
				},
			};

			this.reactService.render(
				this.reactElement,
				DatasetsView(newProps),
				getShadowDomMode(this.accountService.email),
			);
		}
	}

	@action
	updateReactComponent(): void {
		this.renderDatasetView();
	}

	@action
	teardownView(): void {
		this.reactService.unmount(this.reactElement);
		this.eventManagerService.off(AppEvent.ACCESS_TOKEN_REFRESHED, this);
		this.eventManagerService.off(AppEvent.THEME_CHANGED, this);
	}
	<template>
		<div
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
			{{didUpdate this.updateReactComponent @model}}
			id="datasets-container"
		>
		</div>
	</template>
}
